import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useUserStore } from '../../../components/userStore';
import AuthorizedServiceForm from './AuthorizedServiceForm';

const AuthorizedServiceModal = ({
	isOpen = false,
	onCancel,
	externalService = {},
	agencyLinkId,
	onSave,
}) => {
	const modalRef = useRef();
	const { user } = useUserStore();
	const [authorizedServices, setAuthorizedServices] = useState([]);
	const skusQuery = useQuery({
		queryKey: ['service-skus', user.coord_agency.id, externalService.id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/${user.coord_agency.id}/service-skus/${externalService.id}`,
			);
			return data;
		},
		enabled: false,
	});
	const authorizedServiceQuery = useQuery({
		queryKey: ['authorized-service', agencyLinkId, externalService.id],
		queryFn: async () => {
			const { data } = await axios.get(
				`/coord_agencies/authorized-services/${agencyLinkId}`,
				{ params: { extServiceId: externalService.id } },
			);
			return data;
		},
		enabled: false,
	});

	useEffect(() => {
		if (isOpen) {
			modalRef.current.showModal();
			skusQuery.refetch();
			authorizedServiceQuery.refetch().then(({ data }) => {
				setAuthorizedServices(data ?? []);
			});
		} else {
			setAuthorizedServices([]);
		}
	}, [isOpen]);

	const handleAddService = () => {
		setAuthorizedServices([...authorizedServices, {}]);
	};

	const handleUpdateService = (index, updatedService) => {
		const updatedServices = [...authorizedServices];
		updatedServices[index] = updatedService;
		setAuthorizedServices(updatedServices);
	};

	const handleRemoveService = (index) => {
		const updatedServices = [...authorizedServices];
		updatedServices.splice(index, 1);
		setAuthorizedServices(updatedServices);
	};

	const handleSaveServices = () => {
		onSave(
			authorizedServices.map((service) => ({
				...service,
				external_211_services_id: externalService.id,
			})),
		);
	};

	const isNotValid = authorizedServices.some(
		(service) => !service.taxonomy_sku_id,
	);

	return (
		<dialog
			id="auth-service-form-dialog"
			className="modal modal-top"
			ref={modalRef}
		>
			<div className="modal-box">
				<div className="flex flex-row place-content-between">
					<h3>Billing for {externalService?.data?.name}</h3>
					<button
						className="btn btn-primary btn-sm"
						onClick={handleAddService}
						disabled={
							skusQuery.isPending ||
							authorizedServices.length === skusQuery?.data?.length
						}
					>
						Add
					</button>
				</div>
				<div className="grid grid-cols-12 gap-2">
					{authorizedServices.length === 0 && (
						<div className="col-span-12 mt-2">
							No authorized services configured. Click &quot;Add&quot; to create
							one.
						</div>
					)}
					{authorizedServices.map((service, index) => (
						<AuthorizedServiceForm
							key={service.id}
							onFormChange={(service) => handleUpdateService(index, service)}
							serviceData={service || {}}
							exServiceId={externalService?.id}
							hideLabels={index > 0}
							onRemove={() => handleRemoveService(index)}
						/>
					))}
				</div>
				<div className="modal-action">
					<form method="dialog">
						<button className="btn" onClick={onCancel}>
							Cancel
						</button>
						<button
							className="ml-4 btn btn-primary"
							onClick={handleSaveServices}
							disabled={isNotValid}
						>
							Save
						</button>
					</form>
				</div>
			</div>
		</dialog>
	);
};

AuthorizedServiceModal.propTypes = {
	onCancel: PropTypes.func.isRequired,
	isOpen: PropTypes.bool,
	externalService: PropTypes.object,
	agencyLinkId: PropTypes.string,
	onSave: PropTypes.func.isRequired,
};

export default AuthorizedServiceModal;
